// Plain-text addition support after `${...}` placeholder replacement.
// Example: `${i} + ${j}px` becomes `3px` before CSS parsing.
import { collectCssMathFunctionRanges, isIndexInsideRanges } from './arithmetic-ranges'
import { evaluateLoopPlaceholderExpression } from './placeholder-expression'

// RegExp matches spaced numeric and `var(--x)` add/sub chains. Example: `1 + 2px`.
const PLAIN_ADD_SUB_PATTERN = /(?:var\(\s*--[a-zA-Z_][\w-]*\s*\)|-?\d+(?:\.\d+)?(?:[a-zA-Z%]+)?)(?:\s+[+-]\s+(?:var\(\s*--[a-zA-Z_][\w-]*\s*\)|-?\d+(?:\.\d+)?(?:[a-zA-Z%]+)?))+/g

// Evaluate plain-text `a + b` and `a - b` segments outside CSS math functions.
export function evaluatePlainAddSubExpressions(source: string, variables: Record<string, string>): string {
  const cssMathRanges = collectCssMathFunctionRanges(source)

  // Replace eligible plain-text add/sub segments with evaluated values.
  return source.replace(PLAIN_ADD_SUB_PATTERN, (match, offset) => {
    // Keep CSS math function contents untouched.
    if (isIndexInsideRanges(offset, cssMathRanges)) {
      // Return the original match inside CSS math functions.
      return match
    }

    const resolved = resolveVarTokens(match, variables)

    // Keep the original text when `var(--x)` values are unavailable.
    if (resolved === null) {
      // Return the original unresolved candidate text.
      return match
    }

    const value = evaluateLoopPlaceholderExpression(resolved, {})

    // Keep the original text when evaluation does not succeed.
    if (value === null) {
      // Return the original unevaluated candidate text.
      return match
    }

    // Return the evaluated add/sub result.
    return value
  })
}

// Replace all `var(--x)` tokens with their resolved scalar values.
function resolveVarTokens(source: string, variables: Record<string, string>): string | null {
  let output = ''

  // Scan the candidate and inline every `var(--x)` occurrence.
  for (let index = 0; index < source.length; index += 1) {
    if (source[index] !== 'v' || source.slice(index, index + 4) !== 'var(') {
      output += source[index]
      continue
    }

    const end = source.indexOf(')', index + 4)

    // Stop when the `var(...)` token is not closed.
    if (end === -1) {
      // Return null for malformed plain add/sub var tokens.
      return null
    }

    const name = source.slice(index + 4, end).trim()

    // Stop when the `var(...)` token is not a plain custom property name.
    if (!name.startsWith('--') || !(name in variables)) {
      // Return null for unresolved plain add/sub var tokens.
      return null
    }

    output += variables[name]
    index = end
  }

  // Return the candidate with inlined `var(--x)` values.
  return output
}
